import { useEffect, useMemo, useState } from 'react';
import type { PostAnalytics } from '@zen-bell/shared';
import { adminFetch } from '../lib/api';
import { KeywordTable, Kpi } from './ContentAnalytics';
import { LineChart, type Dataset } from './LineChart';
import { RangePicker, lastDays, percent, query, type DateRange } from './RangePicker';

/** Stats panel under the Edit Post form: how this one post performs. */
export function PostStats({ postId }: { postId: string }) {
  const [range, setRange] = useState<DateRange>(() => lastDays(30));
  const [data, setData] = useState<PostAnalytics | null>(null);

  useEffect(() => {
    let live = true;
    adminFetch<PostAnalytics>(`/analytics/posts/${encodeURIComponent(postId)}?${query(range)}`)
      .then((d) => live && setData(d))
      .catch(() => live && setData(null));
    return () => {
      live = false;
    };
  }, [postId, range]);

  const chart = useMemo(() => {
    const daily = data?.daily ?? [];
    const datasets: Dataset[] = [
      { label: 'Views', data: daily.map((d) => d.views), borderColor: '#ffffff', tension: 0.3 },
      { label: 'Download Clicks', data: daily.map((d) => d.cta), borderColor: '#c9a84c', tension: 0.3 },
    ];
    return { labels: daily.map((d) => d.date), datasets };
  }, [data]);

  const t = data?.totals;
  return (
    <section id="post-stats" className="chart-container form-panel">
      <div className="header">
        <h3 className="section-title">Post Stats</h3>
        <div>
          <RangePicker value={range} onChange={setRange} />
        </div>
      </div>
      {!t ? (
        <div className="loading">Fetching data...</div>
      ) : (
        <>
          <div className="stats-grid">
            <Kpi label="Views" value={t.views} hint={`${t.visitors.toLocaleString()} unique visitors`} />
            <Kpi label="App Store" value={t.app_store} />
            <Kpi label="Google Play" value={t.google_play} />
            <Kpi label="Download" value={t.download} />
            <Kpi label="Conversion Rate" value={percent(t.conversion)} />
          </div>
          <LineChart labels={chart.labels} datasets={chart.datasets} />
          <div className="analytics-columns">
            <KeywordTable title="Searches Leading Here" rows={data.keywords} withClicks empty="No visitors came from a search." />
            <div>
              <h3 className="section-title">Traffic Sources</h3>
              <div className="panel table-panel">
                <table className="data-table">
                  <thead><tr><th>Source</th><th className="num">Views</th></tr></thead>
                  <tbody>
                    {data.sources.length === 0 ? (
                      <tr><td colSpan={2} className="empty-state">No views in this range.</td></tr>
                    ) : (
                      data.sources.map((s) => (
                        <tr key={s.source}><td>{s.source}</td><td className="num">{s.views}</td></tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
